import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getAutotestsRuns,
  getAutotestsRunRounds,
  getTestConfigs,
  startAutotest,
} from '../../api/autotests'
import { useI18n } from '../../i18n'
import { useToast } from '../../contexts/ToastContext'
import { FilterGroup, FiltersRow, PageCard, PageSection, Select } from '../../components/ui'
import { AccessDeniedView, DateCell, StatusBadge } from './components'
import { asList, extractTaskId, formatLocalDate, hasRunning, STATUS_ORDER } from './helpers'
import { useAutotestModels } from './runFormHooks'
import '../../styles/pages/page-common.css'
import '../../styles/pages/autotests.css'

export default function AutotestsPage() {
  const { t } = useI18n()
  const { showToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accessDenied, setAccessDenied] = useState(false)

  const [runs, setRuns] = useState([])
  const [statusFilter, setStatusFilter] = useState('')
  const [runsLimit, setRunsLimit] = useState(10)

  // Start test form
  const [modelId, setModelId] = useState('')
  const [configId, setConfigId] = useState('')
  const [numRounds, setNumRounds] = useState('5')
  const [starting, setStarting] = useState(false)

  // Configs
  const [configOptions, setConfigOptions] = useState([])

  // Expanded runs (chat view)
  const [expandedRuns, setExpandedRuns] = useState({})
  const [runRounds, setRunRounds] = useState({})
  const [loadingRounds, setLoadingRounds] = useState({})

  // Track whether we just started a test (for immediate poll)
  const justStartedRef = useRef(false)

  const { modelOptions, reloadModels } = useAutotestModels(t)

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('logs.status.all') },
      ...STATUS_ORDER.map((s) => ({ value: s, label: t(`autotests.status.${s}`) })),
    ],
    [t],
  )

  const loadData = useCallback(
    async (nextStatus = statusFilter, nextLimit = runsLimit, opts = {}) => {
      if (!opts.silent) setLoading(true)
      setError('')
      setAccessDenied(false)
      try {
        const runsRes = await getAutotestsRuns({
          limit: nextLimit, offset: 0, status: nextStatus || undefined, sort: 'id_desc',
        })
        setRuns(asList(runsRes))
      } catch (err) {
        if (err?.status === 401 || err?.status === 403) setAccessDenied(true)
        else setError(err?.message || t('common.error'))
      } finally {
        if (!opts.silent) setLoading(false)
      }
    },
    [runsLimit, statusFilter, t],
  )

  useEffect(() => { loadData() }, [loadData])
  useEffect(() => {
    reloadModels()
    getTestConfigs()
      .then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []
        setConfigOptions([
          { value: '', label: 'Активная конфигурация' },
          ...list.map((c) => ({
            value: String(c.id),
            label: `${c.name}${c.is_active ? ' (активная)' : ''}`,
          })),
        ])
      })
      .catch(() => setConfigOptions([{ value: '', label: 'Активная конфигурация' }]))
  }, [])

  // Auto-refresh: runs list + expanded run rounds (every 3s when any run is RUNNING)
  useEffect(() => {
    const anyRunning = hasRunning(runs)
    const hasJustStarted = justStartedRef.current
    if (!anyRunning && !hasJustStarted) return

    const timer = setInterval(async () => {
      // Refresh runs list
      await loadData(statusFilter, runsLimit, { silent: true })

      // Refresh rounds for expanded running tests
      const expandedRunningIds = runs
        .filter((r) => expandedRuns[r.id] && (r.status === 'RUNNING' || r.status === 'PENDING'))
        .map((r) => r.id)

      for (const runId of expandedRunningIds) {
        try {
          const rounds = await getAutotestsRunRounds(runId)
          setRunRounds((prev) => ({ ...prev, [runId]: asList(rounds) }))
        } catch {
          // ignore
        }
      }

      // Clear justStarted after first successful poll
      if (hasJustStarted) justStartedRef.current = false
    }, 3000)

    return () => clearInterval(timer)
  }, [loadData, runs, runsLimit, statusFilter, expandedRuns])

  async function handleStartTest(e) {
    e.preventDefault()
    if (!modelId) return
    setStarting(true)
    try {
      const res = await startAutotest({
        model_id: Number(modelId),
        num_rounds: Number(numRounds) || 5,
        config_id: configId ? Number(configId) : null,
      })
      const taskId = extractTaskId(res)
      showToast(taskId ? `Тест запущен: ${taskId.slice(0, 8)}…` : 'Тест запущен', 'success')

      // Mark to trigger immediate polling
      justStartedRef.current = true

      // Immediately reload to show the new run
      await loadData(statusFilter, runsLimit, { silent: true })
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) setAccessDenied(true)
      else showToast(err?.message || t('common.error'), 'error')
    } finally {
      setStarting(false)
    }
  }

  async function toggleRunExpanded(runId) {
    if (expandedRuns[runId]) {
      setExpandedRuns((prev) => ({ ...prev, [runId]: false }))
      return
    }
    setExpandedRuns((prev) => ({ ...prev, [runId]: true }))
    // Always fetch latest rounds when expanding
    setLoadingRounds((prev) => ({ ...prev, [runId]: true }))
    try {
      const rounds = await getAutotestsRunRounds(runId)
      setRunRounds((prev) => ({ ...prev, [runId]: asList(rounds) }))
    } catch {
      setRunRounds((prev) => ({ ...prev, [runId]: [] }))
    } finally {
      setLoadingRounds((prev) => ({ ...prev, [runId]: false }))
    }
  }

  if (accessDenied) return <AccessDeniedView />

  return (
    <PageSection
      className="autotests-page"
      title="Автотесты"
      subtitle="Запуск тестовых диалогов и просмотр результатов"
    >
      {error && <p className="form-error">{error}</p>}

      {/* Start test card */}
      <PageCard className="autotests-start-card">
        <form className="autotests-start-form" onSubmit={handleStartTest}>
          <div className="autotests-start-fields">
            <div className="autotests-start-field">
              <label className="form-label">Модель</label>
              <Select
                className="autotests-select"
                value={modelId}
                onChange={(v) => setModelId(v)}
                options={modelOptions}
                dropdownPosition="down"
              />
            </div>
            <div className="autotests-start-field">
              <label className="form-label">Конфигурация</label>
              <Select
                className="autotests-select"
                value={configId}
                onChange={(v) => setConfigId(v)}
                options={configOptions}
                dropdownPosition="down"
              />
            </div>
            <div className="autotests-start-field autotests-start-field--small">
              <label className="form-label">Раундов</label>
              <input
                type="number"
                min={1}
                value={numRounds}
                onChange={(e) => setNumRounds(e.target.value)}
                className="autotests-rounds-input"
              />
            </div>
            <div className="autotests-start-field autotests-start-field--btn">
              <button type="submit" className="btn btn-primary" disabled={!modelId || starting}>
                {starting ? '⏳ Запуск…' : '▶ Запустить тест'}
              </button>
            </div>
          </div>
        </form>
      </PageCard>

      {/* Filter */}
      <FiltersRow>
        <FilterGroup label={t('autotests.filters.status') || 'Статус'}>
          <Select
            className="filter-select"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value || '')
              loadData(value || '', runsLimit)
            }}
            options={statusOptions}
            dropdownPosition="down"
          />
        </FilterGroup>
      </FiltersRow>

      {/* Runs list */}
      <div className="autotests-runs-list">
        {loading && runs.length === 0 ? (
          <p className="autotests-muted">{t('common.loading')}</p>
        ) : runs.length === 0 ? (
          <PageCard className="autotests-panel">
            <p className="autotests-muted">Нет запусков</p>
          </PageCard>
        ) : (
          runs.map((run) => (
            <PageCard key={run.id} className={`autotests-run-card ${expandedRuns[run.id] ? 'autotests-run-card--expanded' : ''}`}>
              <div
                className="autotests-run-header"
                onClick={() => toggleRunExpanded(run.id)}
                role="button"
                tabIndex={0}
              >
                <div className="autotests-run-info">
                  <span className="autotests-run-id">#{run.id}</span>
                  <span className="autotests-run-name">{run.scenario_name || 'Quick Test'}</span>
                  <StatusBadge status={run.status} />
                </div>
                <div className="autotests-run-meta">
                  <DateCell iso={run.created_at || run.started_at} />
                  <span className={`autotests-chevron ${expandedRuns[run.id] ? 'autotests-chevron--open' : ''}`}>▸</span>
                </div>
              </div>

              {expandedRuns[run.id] && (
                <div className="autotests-chat-container">
                  {loadingRounds[run.id] ? (
                    <p className="autotests-muted autotests-chat-loading">Загрузка диалога…</p>
                  ) : (runRounds[run.id] || []).length === 0 ? (
                    <p className="autotests-muted autotests-chat-empty">Нет данных диалога</p>
                  ) : (
                    <div className="autotests-chat">
                      {(runRounds[run.id] || []).map((round, i) => (
                        <div key={i} className="autotests-chat-round">
                          <div className="autotests-chat-msg autotests-chat-msg--fan">
                            <div className="autotests-chat-avatar autotests-chat-avatar--fan">👤</div>
                            <div className="autotests-chat-bubble autotests-chat-bubble--fan">
                              <div className="autotests-chat-role">Фан</div>
                              <div className="autotests-chat-text">
                                {round.fan_message || round.input_message || '—'}
                              </div>
                            </div>
                          </div>
                          <div className="autotests-chat-msg autotests-chat-msg--bot">
                            <div className="autotests-chat-bubble autotests-chat-bubble--bot">
                              <div className="autotests-chat-role">Бот</div>
                              <div className="autotests-chat-text">
                                {round.bot_response || round.output_message || '—'}
                              </div>
                              {round.state_after && (
                                <div className="autotests-chat-state">
                                  Состояние: <strong>{round.state_after}</strong>
                                </div>
                              )}
                            </div>
                            <div className="autotests-chat-avatar autotests-chat-avatar--bot">🤖</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(run.error || run.report?.error) && (
                    <div className="autotests-run-error">
                      <strong>Ошибка:</strong> {run.error || run.report?.error}
                    </div>
                  )}
                </div>
              )}
            </PageCard>
          ))
        )}
      </div>

      {runs.length >= runsLimit && (
        <div className="autotests-actions-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              const next = runsLimit + 10
              setRunsLimit(next)
              loadData(statusFilter, next)
            }}
          >
            {t('common.loadMore')}
          </button>
        </div>
      )}
    </PageSection>
  )
}
